import { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import App from './App';
import { portalApi } from './portalApi';
import './Portal.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

const menu = [
  ['dashboard', '대시보드', 'grid'],
  ['subjects', '대상자 관리', 'users'],
  ['algorithm', '맞춤형 사례관리', 'spark'],
  ['reports', '결과 보고서', 'file'],
  ['accounts', '사용자·권한', 'shield'],
];

const roleLabel = { SUPER_ADMIN: '최고관리자', ORG_ADMIN: '기관관리자', CASE_MANAGER: '사례관리자', VIEWER: '조회자' };
const statusLabel = { ACTIVE: '관리중', HOLD: '보류', CLOSED: '종결' };

function Icon({ name, size = 20 }) {
  const paths = {
    grid: <><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    spark: <><path d="m12 3-1.6 4.4L6 9l4.4 1.6L12 15l1.6-4.4L18 9l-4.4-1.6L12 3Z" /><path d="m5 14-.8 2.2L2 17l2.2.8L5 20l.8-2.2L8 17l-2.2-.8L5 14Z" /><path d="m19 14-.8 2.2L16 17l2.2.8L19 20l.8-2.2L22 17l-2.2-.8L19 14Z" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></>,
    exit: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></>,
  };
  return <svg className="portal-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('admin@demo.local');
  const [password, setPassword] = useState('Demo1234!');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try { onLogin(await portalApi.login({ email, password })); }
    catch (nextError) { setError(nextError.message); }
    finally { setLoading(false); }
  };
  return <main className="portal-login">
    <section className="login-brand-panel">
      <div className="brand-mark large">온</div>
      <div>
        <span className="brand-kicker">CHUNGNAM DEMENTIA CENTER</span>
        <h1>사례를 이해하고,<br /><em>필요한 돌봄을 연결합니다.</em></h1>
        <p>평가부터 맞춤형 서비스 계획, 결과 보고까지<br />한 흐름으로 관리하는 사례관리 지원 시스템입니다.</p>
      </div>
      <div className="login-security"><Icon name="shield" /><span><strong>개인정보 보호 설계</strong><small>역할 기반 접근 · 감사 기록 · 암호화 저장</small></span></div>
    </section>
    <section className="login-form-panel">
      <form className="portal-login-card" onSubmit={submit}>
        <div className="mobile-brand"><span className="brand-mark">온</span><strong>온케어</strong></div>
        <span className="page-kicker">안전한 업무 공간</span>
        <h2>로그인</h2>
        <p>등록된 기관 계정으로 접속해 주세요.</p>
        <label>이메일<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" /></label>
        <label>비밀번호<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" /></label>
        {error && <div className="portal-alert">{error}</div>}
        <button className="portal-primary login-button" disabled={loading}>{loading ? '인증 중…' : '로그인'}</button>
        <div className="demo-account"><strong>테스트 계정</strong><span>admin@demo.local</span><span>Demo1234!</span></div>
        <small className="login-footnote">실제 운영 전 HTTPS, 비밀키 관리, 개인정보 처리방침을 적용하세요.</small>
      </form>
    </section>
  </main>;
}

function Dashboard({ stats, goSubjects, goAlgorithm }) {
  const lineData = {
    labels: stats.statusSeries.map((item) => item.label),
    datasets: [{ data: stats.statusSeries.map((item) => item.value), borderColor: '#1c7c7d', backgroundColor: 'rgba(28,124,125,.12)', fill: true, tension: .42, pointRadius: 3, pointBackgroundColor: '#1c7c7d' }],
  };
  const doughnutData = {
    labels: stats.serviceSeries.map((item) => item.label),
    datasets: [{ data: stats.serviceSeries.map((item) => item.value), backgroundColor: ['#174767', '#1c7c7d', '#76a9a8', '#e1ad63'], borderWidth: 0, hoverOffset: 4 }],
  };
  const cards = [
    ['관리 대상자', stats.activeSubjects, '전월 대비 +6명', 'people'],
    ['이달 수립 계획', stats.monthlyPlans, '목표 22건 중 82%', 'plan'],
    ['계획 완료율', `${stats.completionRate}%`, '기관 평균 78%', 'rate'],
    ['점검 필요', stats.overdueCases, '오늘 확인 권장', 'alert'],
  ];
  return <>
    <header className="portal-page-head">
      <div><span className="page-kicker">OVERVIEW</span><h1>안녕하세요, 오늘의 사례관리 현황입니다.</h1><p>2026년 7월 24일 금요일 · 충청남도광역치매센터</p></div>
      <button className="portal-primary" onClick={goAlgorithm}><Icon name="plus" size={18} /> 새 사례계획</button>
    </header>
    <section className="metric-grid">
      {cards.map(([label, value, note, tone]) => <article className={`metric-card tone-${tone}`} key={label}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>)}
    </section>
    <section className="dashboard-grid">
      <article className="portal-card chart-wide">
        <div className="card-head"><div><h2>월별 사례계획 수립</h2><p>최근 6개월 최종 저장 건수</p></div><span className="status-pill active">정상 운영</span></div>
        <div className="line-chart"><Line data={lineData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { stepSize: 5 }, grid: { color: '#edf1f3' } } } }} /></div>
      </article>
      <article className="portal-card">
        <div className="card-head"><div><h2>서비스 구성</h2><p>진행 중 계획 기준</p></div></div>
        <div className="doughnut-wrap"><Doughnut data={doughnutData} options={{ cutout: '70%', plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 18 } } } }} /><div className="doughnut-total"><strong>98</strong><span>서비스</span></div></div>
      </article>
      <article className="portal-card quick-card">
        <div className="card-head"><div><h2>빠른 업무</h2><p>자주 사용하는 기능</p></div></div>
        <button onClick={goSubjects}><span><Icon name="users" /><b>대상자 조회</b></span><small>등록 대상자 검색·관리</small></button>
        <button onClick={goAlgorithm}><span><Icon name="spark" /><b>맞춤형 계획 수립</b></span><small>14개 영역 평가 및 추천</small></button>
      </article>
      <article className="portal-card activity-card">
        <div className="card-head"><div><h2>최근 업무</h2><p>기관 내 최신 처리 내역</p></div></div>
        {[['김○희', '사례계획 v2 최종 저장', '10분 전'], ['이○수', '초기 평가 임시저장', '1시간 전'], ['최○자', '서비스 점검일 변경', '어제']].map((row) => <div className="activity-row" key={row[0]}><span className="avatar">{row[0][0]}</span><span><b>{row[0]}</b><small>{row[1]}</small></span><time>{row[2]}</time></div>)}
      </article>
    </section>
  </>;
}

function Subjects({ items, search, setSearch, onAdd, onPlan }) {
  return <>
    <header className="portal-page-head">
      <div><span className="page-kicker">SUBJECTS</span><h1>대상자 관리</h1><p>대상자의 기본정보와 사례관리 상태를 확인합니다.</p></div>
      <button className="portal-primary" onClick={onAdd}><Icon name="plus" size={18} /> 대상자 등록</button>
    </header>
    <section className="portal-card">
      <div className="table-tools"><div className="portal-search"><Icon name="search" size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="안심등록번호, 대상자명, 담당자 검색" /></div><span>총 <b>{items.length}</b>명</span></div>
      <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>대상자</th><th>안심등록번호</th><th>출생연도</th><th>담당자</th><th>관리 상태</th><th>최근 수정</th><th></th></tr></thead><tbody>
        {items.map((subject) => <tr key={subject.id}><td><div className="subject-name"><span className="avatar">{subject.name[0]}</span><b>{subject.name}</b></div></td><td>{subject.registrationNo}</td><td>{subject.birthYear || '-'}년</td><td>{subject.managerName}</td><td><span className={`status-pill ${subject.status.toLowerCase()}`}>{statusLabel[subject.status]}</span></td><td>{new Date(subject.updatedAt).toLocaleDateString('ko-KR')}</td><td><button className="table-action" onClick={() => onPlan(subject)}>계획 수립</button></td></tr>)}
      </tbody></table></div>
    </section>
  </>;
}

function Accounts({ users }) {
  return <>
    <header className="portal-page-head"><div><span className="page-kicker">ACCESS CONTROL</span><h1>사용자·권한 관리</h1><p>기관 계정의 역할과 사용 상태를 관리합니다.</p></div><button className="portal-primary"><Icon name="plus" size={18} /> 사용자 초대</button></header>
    <section className="portal-card access-note"><Icon name="shield" size={24} /><div><b>최소 권한 원칙</b><p>사례관리자는 담당 업무를, 조회자는 승인된 통계와 기록만 볼 수 있습니다. 권한 변경은 감사로그에 남습니다.</p></div></section>
    <section className="portal-card"><div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>사용자</th><th>이메일</th><th>기관</th><th>권한</th><th>상태</th></tr></thead><tbody>{users.map((user) => <tr key={user.id}><td><div className="subject-name"><span className="avatar">{user.name[0]}</span><b>{user.name}</b></div></td><td>{user.email}</td><td>{user.institution}</td><td><span className="role-pill">{roleLabel[user.role]}</span></td><td><span className={`status-pill ${user.active ? 'active' : 'closed'}`}>{user.active ? '사용 중' : '중지'}</span></td></tr>)}</tbody></table></div></section>
  </>;
}

function Reports({ goAlgorithm }) {
  return <>
    <header className="portal-page-head"><div><span className="page-kicker">REPORTS</span><h1>결과 보고서</h1><p>최종 확정된 사례계획을 PDF 또는 Word 문서로 생성합니다.</p></div></header>
    <section className="report-hero portal-card"><div className="report-art"><Icon name="file" size={52} /></div><div><span className="status-pill active">서버 생성 방식</span><h2>개인정보가 포함된 문서는 서버에서 안전하게 생성합니다.</h2><p>최종 계획의 버전과 생성자를 기록하고, 다운로드 파일의 무결성을 확인할 수 있도록 체크섬을 보관합니다.</p><button className="portal-primary" onClick={goAlgorithm}>사례계획 작성하기</button></div></section>
    <section className="report-grid"><article className="portal-card"><b>PDF 보고서</b><p>배포·인쇄에 적합한 고정 레이아웃 결과물</p><span>PDFKit 서버 렌더링</span></article><article className="portal-card"><b>Word 보고서</b><p>기관 양식에 맞춰 추가 편집할 수 있는 문서</p><span>DOCX 표준 문서 생성</span></article></section>
  </>;
}

function NewSubjectModal({ onClose, onSave }) {
  const [form, setForm] = useState({ registrationNo: '', name: '', birthYear: '', gender: 'U' });
  return <div className="portal-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <form className="portal-modal" onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
      <div className="modal-head"><div><span className="page-kicker">NEW SUBJECT</span><h2>대상자 등록</h2></div><button type="button" onClick={onClose} aria-label="닫기">×</button></div>
      <div className="modal-grid"><label>안심등록번호<input required value={form.registrationNo} onChange={(event) => setForm({ ...form, registrationNo: event.target.value })} placeholder="CN-2026-0000" /></label><label>대상자 성명<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>출생연도<input type="number" min="1900" max="2026" value={form.birthYear} onChange={(event) => setForm({ ...form, birthYear: event.target.value })} /></label><label>성별<select value={form.gender} onChange={(event) => setForm({ ...form, gender: event.target.value })}><option value="U">미상/미입력</option><option value="F">여성</option><option value="M">남성</option></select></label></div>
      <p className="modal-privacy">민감정보는 목적에 필요한 최소 범위로만 입력하세요.</p>
      <div className="modal-actions"><button type="button" className="portal-secondary" onClick={onClose}>취소</button><button className="portal-primary">등록</button></div>
    </form>
  </div>;
}

function Portal() {
  const [session, setSession] = useState(portalApi.session);
  const [page, setPage] = useState('dashboard');
  const [stats, setStats] = useState({ activeSubjects: 0, monthlyPlans: 0, completionRate: 0, overdueCases: 0, statusSeries: [], serviceSeries: [] });
  const [subjects, setSubjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!session) return;
    portalApi.stats().then(setStats);
    portalApi.subjects().then(setSubjects);
    if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(session.user.role)) portalApi.users().then(setUsers);
  }, [session]);

  const visibleSubjects = useMemo(() => subjects.filter((item) => `${item.name} ${item.registrationNo} ${item.managerName}`.toLowerCase().includes(search.toLowerCase())), [subjects, search]);
  if (!session) return <Login onLogin={setSession} />;
  if (page === 'algorithm') return <App initialUser={session.user} onExit={() => setPage('dashboard')} />;

  const changePage = (nextPage) => { setPage(nextPage); window.scrollTo(0, 0); };
  const addSubject = async (form) => {
    const created = await portalApi.createSubject(form);
    setSubjects((previous) => [created, ...previous]);
    setShowModal(false);
  };
  return <div className="portal-shell">
    <aside className="portal-sidebar">
      <div className="portal-brand"><span className="brand-mark">온</span><span><strong>온케어</strong><small>맞춤형 사례관리</small></span></div>
      <nav>{menu.filter(([id]) => id !== 'accounts' || ['SUPER_ADMIN', 'ORG_ADMIN'].includes(session.user.role)).map(([id, label, icon]) => <button className={page === id ? 'active' : ''} onClick={() => changePage(id)} key={id}><Icon name={icon} /><span>{label}</span>{id === 'algorithm' && <em>AI</em>}</button>)}</nav>
      <div className="portal-sidebar-foot"><div className="privacy-chip"><Icon name="shield" size={16} /><span>개인정보 보호 모드</span></div><small>v1.0 · TEST SERVER</small></div>
    </aside>
    <div className="portal-main">
      <header className="portal-topbar"><div className="top-search"><Icon name="search" size={17} /><span>통합 검색</span><kbd>⌘ K</kbd></div><div className="top-user"><button className="icon-button" aria-label="알림"><Icon name="bell" size={19} /><i /></button><div className="avatar">{session.user.name[0]}</div><span><b>{session.user.name}</b><small>{roleLabel[session.user.role]}</small></span><button className="logout-button" onClick={() => { portalApi.logout(); setSession(null); }} aria-label="로그아웃"><Icon name="exit" size={18} /></button></div></header>
      <main className="portal-content">
        {page === 'dashboard' && <Dashboard stats={stats} goSubjects={() => changePage('subjects')} goAlgorithm={() => changePage('algorithm')} />}
        {page === 'subjects' && <Subjects items={visibleSubjects} search={search} setSearch={setSearch} onAdd={() => setShowModal(true)} onPlan={() => changePage('algorithm')} />}
        {page === 'reports' && <Reports goAlgorithm={() => changePage('algorithm')} />}
        {page === 'accounts' && <Accounts users={users} />}
      </main>
    </div>
    {showModal && <NewSubjectModal onClose={() => setShowModal(false)} onSave={addSubject} />}
  </div>;
}

export default Portal;

